import {
  FluidAlignment,
  FluidContextMenuItemType,
  FluidContextMenuLink,
  FluidContextMenuSection,
  FluidContextMenuToggle,
  FluidKeyedObject,
  FluidMenuSectionStyle,
  FluidTheme,
  FluidThemeInterface,
  FluidToggleType,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { createPopper, Instance } from '@popperjs/core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { DropdownMenu } from '../../common/fluid-dropdown-menu.components';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { ESCAPE, EventUtils } from '../../utils/events/event-utils';
import { PopperJSModifiers } from '../../utils/popperjs/popperjs.class';
import {
  hasPopperAncestor,
  matchWidthModifier,
  togglePopperListeners,
} from '../../utils/popperjs/popperjs.utils';
import { Utils } from '../../utils/utils';
import { ToggleAllValue } from '../fluid-toggle-group/components/fluid-toggle-group.components';
import { ContextMenuContent } from './components/fluid-context-menu.components';

/**
 * @displayName Context Menu
 *
 * @contextData FLUID Context Menu is a modular dropdown menu that is attached to a "trigger" element such as an Icon or Button. Context Menu consists of individual "menu sections" that can contain hyperlinks, FLUID Toggles, or a FLUID Toggle Group. For ultimate customization, menu sections can also be configured to accept slotted content.
 *
 * Context Menu should be used for contextual options, actions, or controls that relate to nearby content. For example, FLUID Table uses Context Menu for its column filter menus.
 *
 * @slot - Default slot for "trigger" element (NOTE: if "trigger" element is a Button, set "asWrapper" Prop to true)
 * @slot CUSTOM_SLOT_NAME - Custom named slot generated from "slotName" property (FluidContextMenuSection)
 */
@Component({
  tag: 'fluid-context-menu',
  styleUrl: 'fluid-context-menu.less',
  shadow: true,
})
export class FluidContextMenu
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to PopperJS instance.
   */
  private _popperInstance: Instance;

  /**
   * True if Context Menu has PopperJS applied to an ancestor element,
   * false if not. When true, Popper's "strategy" needs to be set to
   * "absolute" so dropdown menu displays correctly (for some reason,
   * having a "fixed" Popper element as a child of another Popper element
   * with "fixed" strategy skews Popper's "translate" styles & causes the
   * element to appear offscreen).
   */
  private _hasPopperAncestor: boolean;

  /**
   * Reference to the default slot's "trigger element wrapper" button
   * (used for PopperJS instance).
   */
  private _triggerRef: HTMLElement;

  /**
   * Reference to dropdown menu container root element.
   */
  private _containerRef: HTMLElement;

  /**
   * References to any Toggle Groups rendered (if they exist). Used
   * for "resetToggles()" method.
   */
  private _toggleGroupRefs: FluidKeyedObject<HTMLFluidToggleGroupElement> = {};

  /**
   * Whether Context Menu contains Toggles. If true, Context Menu will
   * disable "click outside to close" functionality by default &
   * display a "close" button.
   */
  private _hasToggles = false;

  /**
   * Boolean that tracks whether mouse is currently within the bounds
   * of the component (i.e. both "trigger" & menu elements)
   */
  private _mouseInBounds = false;

  /**
   * Delay (in ms) before menu closes. Only used if menu was opened
   * via hover or keyboard navigation.
   */
  private _closeDelay = 450;

  /**
   * Reference to setTimeout used for Context Menu close delay.
   */
  private _closeTimeout;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidContextMenuElement;

  // ================================================================ //
  // -- States

  /**
   * Boolean that controls whether the Context Menu is visible or hidden.
   */
  @State() _menuOpen = false;

  /**
   * Used to remove dropdown menu from the DOM when closed. Set to false
   * when menu is open, then set to true AFTER menu's fade-out animation
   * completes. Used to prevent FOUC when Context Menu is first rendered
   * & to prevent closed menu from inadvertently affecting DOM layout.
   */
  @State() _menuHidden = true;

  /**
   * The final derived state of the Context Menu sections used for rendering.
   */
  @State() _menuSections: FluidContextMenuSection[];

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Unique "id" attribute for the dropdown menu container.
   */
  @Prop({ mutable: true }) menuId: string = generateUniqueId();

  /**
   * Array of FluidContextMenuSection objects that populate the Context
   * Menu.
   */
  @Prop({ mutable: true }) menuSections: FluidContextMenuSection[];

  @Watch('menuSections')
  onMenuSectionsChange(
    newSections: FluidContextMenuSection[],
    oldSections: FluidContextMenuSection[]
  ) {
    if (Utils.isEqual(newSections, oldSections)) return;
    this.prepareContextMenu();
  }

  /**
   * If true, menu will be disabled & "trigger" element will be inert.
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * Indicates that Context Menu "trigger" slot contains a clickable
   * / focusable element (e.g. FLUID Button). Prevents "trigger" slot
   * wrapper from receiving "double :focus" via keyboard navigation.
   * NOTE: if using FLUID Button for "trigger", make sure to set Prop
   * "removeMargin" to true.
   */
  @Prop({ mutable: true }) asWrapper: boolean;

  /**
   * If Context Menu contains Toggles, enables "click outside to close"
   * functionality & prevents default "close" button from rendering when
   * true. If "openOnHover" is true, also allows Context Menu to close
   * automatically when mouse leaves the component bounds. NOTE:
   * this Prop is only necessary if Context Menu contains Toggles -
   * "click outside to close" functionality is enabled by default when
   * no Toggles exist.
   */
  @Prop({ mutable: true }) outsideClickClose: boolean;

  /**
   * Sets dropdown menu alignment with the "trigger" element (i.e. "left"
   * will align the left edge of the dropdown menu with left edge of the
   * "trigger" element).
   */
  @Prop({ mutable: true }) menuAlignment: Exclude<
    FluidAlignment,
    FluidAlignment.CENTER
  > = FluidAlignment.LEFT;

  /**
   * Changes the dropdown menu's CSS "position" property from "fixed"
   * (default) to "absolute". NOTE: if the dropdown menu isn't displaying
   * correctly, try setting this Prop's value to "true".
   */
  @Prop({ mutable: true }) absolutePosition: boolean;

  @Watch('menuAlignment')
  @Watch('absolutePosition')
  onPopperOptionsChange() {
    this.updatePopperOptions();
  }

  /**
   * Determines how menu sections are displayed within the dropdown menu.
   * "stacked" causes menu sections to cascade vertically (i.e. column
   * layout). "inline" displays menu sections side-by-side horizontally
   * (i.e. row layout).
   */
  @Prop({ mutable: true }) menuSectionStyle: FluidMenuSectionStyle =
    FluidMenuSectionStyle.STACKED;

  /**
   * When true, hovering over the "trigger" element will open the menu
   * and moving the mouse out of the component's bounds will close it.
   * NOTE: this Prop DISABLES click events on the "trigger" element (i.e.
   * clicking the trigger will NOT open/close the menu), but does NOT
   * affect keyboard events (i.e. "enter" or "space" WILL open the menu).
   */
  @Prop({ mutable: true }) openOnHover: boolean;

  /**
   * When true, prevents Context Menu from transferring focus to the
   * dropdown menu when opened. This is useful when the "trigger"
   * element can receive user input (e.g. Input Field) & needs to
   * maintain focus after the menu is opened.
   */
  @Prop({ mutable: true }) disableFocusTransfer: boolean;

  /**
   * When true, changes Context Menu's layout from "inline-block"
   * to "block" (i.e. full-width) & changes the dropdown menu's
   * edge-detection behavior to be consistent with FLUID Combo Box's
   * dropdown menu. Useful when Context Menu is used with a "trigger"
   * element that should span the full width of its parent container
   * (e.g. FLUID Input Field).
   */
  @Prop({ mutable: true }) block: boolean;

  @Watch('block')
  onBlockChange() {
    this.updatePopperOptions();
  }

  /**
   * The label to use for the close button
   */
  @Prop({ mutable: true }) closeLabel = 'Close';

  // ================================================================ //
  // -- Events

  /**
   * Event emitted each time Context Menu is hidden or shown. Contains
   * a boolean that is true if menu is open & false if menu is closed.
   */
  @Event() contextMenuToggled: EventEmitter<boolean>;

  /**
   * Event emitted each time a link is clicked. Contains the
   * FluidContextMenuLink object of the link that was clicked.
   */
  @Event() linkClicked: EventEmitter<FluidContextMenuLink>;

  /**
   * Event emitted each time a Toggle changes. Contains the entire
   * FluidContextMenuSection object of the Toggle that was changed so
   * the calling component has access to the status of ALL Toggles
   * within the menu section.
   */
  @Event() toggleChanged: EventEmitter<FluidContextMenuSection[]>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setZIndex - Set component z-index relative to global base value
   */
  connectedCallback(): void {
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: prepareContextMenu - initialize the Context Menu state
   */
  componentWillLoad() {
    this.prepareContextMenu();
  }

  /**
   * Called once just after the component fully loaded.
   * @action: createPopperInstance - initialize PopperJS instance
   */
  componentDidLoad() {
    this.createPopperInstance();
  }

  /**
   * Called when Prop/State changes & re-render is about to be requested.
   * When "false" is explicitly returned, the component will NOT re-render.
   * When "true" OR "undefined" is returned, the component WILL re-render.
   */
  componentShouldUpdate(
    newValue: any,
    oldValue: any,
    propName: string
  ): boolean | void {
    // When "menuSections" Prop changes, only trigger re-render if necessary
    if (propName === 'menuSections') {
      return !Utils.isEqual(newValue, oldValue);
    }
    // When any other Prop/State changes, always trigger re-render (i.e. return "undefined")
  }

  /**
   * Called when component will be updated due to @Prop or @State change.
   * @action: setPopperStrategy - sets Popper strategy to "absolute" if an ancestor Popper element exists
   * @action: createPopperInstance - creates PopperJS instance if not already defined
   * @action: update - update PopperJS instance before upcoming render
   */
  componentWillUpdate() {
    this.setPopperStrategy();

    if (!this._popperInstance) {
      this.createPopperInstance();
    } else {
      this._popperInstance?.update().then();
    }
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action: destroy - removes PopperJS instance
   */
  disconnectedCallback() {
    this._popperInstance?.destroy();
    this._popperInstance = undefined;
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Event listener for external clicks (i.e. outside Context Menu).
   * Closes Context Menu if menu is open & "outsideClickClose" is true.
   */
  @Listen('click', { target: 'body' })
  onBodyClick(event) {
    if (
      !this.outsideClickClose ||
      !this._menuOpen ||
      EventUtils.eventPathIncludes(event, this.host)
    )
      return;
    this.toggleMenu();
  }

  /**
   * Listener for keydown events that closes the dropdown menu when
   * the escape key is pressed.
   */
  @Listen('keydown')
  onKeyDown(e: KeyboardEvent) {
    if (this.disabled) return;
    EventUtils.isKey(e, ESCAPE) && this.toggleMenu(false);
  }

  /**
   * Listener that determines when `:focus` enters the component.
   * Clears "close" delay timeout.
   */
  @Listen('focusin', { capture: true })
  onFocusIn() {
    clearTimeout(this._closeTimeout);
  }

  /**
   * Listener that determines when `:focus` exits the component.
   * Closes the menu after a delay IF mouse is NOT within bounds
   * of the component.
   */
  @Listen('focusout', { capture: true })
  onFocusOut() {
    if (!this.outsideClickClose) return;
    clearTimeout(this._closeTimeout);

    if (!this._mouseInBounds) {
      this.delayCloseMenu();
    }
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Opens or closes the dropdown menu, emits "contextMenuToggled" event,
   * & moves `:focus` to the dropdown menu container (if open). Exposed
   * publicly so that menu can be toggled externally.
   */
  @Method()
  async toggleMenu(open = !this._menuOpen) {
    if (this.disabled || this._menuOpen === open) return;
    this._menuOpen = open;
    if (this._menuOpen) this._menuHidden = false;
    togglePopperListeners(this._popperInstance, this._menuOpen);
    this.contextMenuToggled.emit(this._menuOpen);

    if (!this.disableFocusTransfer) {
      await Utils.nextTick();
      this._menuOpen && this._containerRef?.focus();
    }
  }

  /**
   * Resets all Toggle Groups in the Context Menu to the given toggle
   * state (i.e. all Toggles will be checked when "toggleState" is true,
   * & unchecked when false).
   */
  @Method()
  async resetToggles(toggleState = true) {
    return Promise.all(
      Object.keys(this._toggleGroupRefs).map((sectionId) =>
        this._toggleGroupRefs[sectionId].toggleAll(toggleState)
      )
    );
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Closes the dropdown menu after a delay. Triggered when menu was
   * opened via hover or keyboard navigation.
   */
  delayCloseMenu(): void {
    this._closeTimeout = setTimeout(() => {
      this.toggleMenu(false);
    }, this._closeDelay);
  }

  /**
   * Checks all ancestor elements for PopperJS - if Context Menu is
   * a child of another Popper element, sets Popper strategy to
   * "absolute" to ensure dropdown menu displays correctly (for some
   * reason, having a "fixed" Popper element as a child of another Popper
   * element with "fixed" strategy skews Popper's "translate" styles &
   * causes the element to appear offscreen).
   */
  setPopperStrategy(): void {
    if (!isNullOrUndefined(this._hasPopperAncestor) || !this._menuOpen) return;
    this._hasPopperAncestor = hasPopperAncestor(this.host);
    if (this._hasPopperAncestor) {
      this.absolutePosition = true;
    }
  }

  /**
   * Prepares the final menu section state & determines "click outside
   * to close" functionality. If Context Menu contains Toggles / Toggle
   * Group(s) & "outsideClickClose" is NOT provided, "click outside to
   * close" functionality will be DISABLED & "close" button will be
   * rendered.
   */
  prepareContextMenu(): void {
    this.initializeMenuSections();
    this.findMenuToggles();
    this.outsideClickClose = this.outsideClickClose ?? !this._hasToggles;
  }

  /**
   * Sets unique IDs for each menu section & Toggle. Also converts
   * FluidContextMenuToggle objects into correct format for FLUID Toggle.
   */
  initializeMenuSections(): void {
    this._menuSections = (this.menuSections || []).map((menuSection) => {
      const section = {
        ...menuSection,
        id: menuSection.id || generateUniqueId(),
      };
      if (section.toggles) {
        // Make compatible with FluidToggleConfig
        section.toggles = section.toggles.map((toggle) =>
          this.initializeToggleConfig(toggle)
        );
      }
      return section;
    });
  }

  /**
   * Converts FluidContextMenuToggle objects into a compatible format
   * for FLUID Toggle's "config" Prop. Triggers deprecation warning if
   * "toggleIconOn" or "toggleIconOff" properties are used. NOTE:
   * FluidContextMenuToggle "selected" property overrides "initialValue"
   * & "toggled" properties - "initialValue" & "toggled" properties are
   * consolidated into "selected" property for simplicity & consistency.
   * @param config - FluidContextMenuToggle object to be converted
   * @return - object compatible with FLUID Toggle's "config" Prop
   */
  initializeToggleConfig(
    config: FluidContextMenuToggle
  ): FluidContextMenuToggle {
    const {
      id,
      selected,
      initialValue,
      toggled,
      toggleIconOn,
      toggleIconOff,
      ...props
    } = config;
    const toggleId = id || generateUniqueId();

    const setCustomToggleConfig = !!toggleIconOn || !!toggleIconOff;
    if (setCustomToggleConfig) {
      Utils.deprecationWarning(
        this.host,
        'FluidContextMenuToggle',
        'menuSections.toggles.toggleIconOn/toggleIconOff',
        'menuSections.customToggleConfig',
        'FluidCustomToggleConfig'
      );
    }

    return {
      ...props,
      id: toggleId,
      controlName: toggleId, // Toggle ID is set via `controlName`, so map ID to `controlName`
      selected:
        selected ?? (initialValue as boolean) ?? (toggled as boolean) ?? false,
      ...(setCustomToggleConfig && {
        toggleType: props.toggleType ?? FluidToggleType.CUSTOM,
        customToggleConfig: {
          iconOn: toggleIconOn,
          iconOff: toggleIconOff,
        },
      }),
    };
  }

  /**
   * Determines if the Context Menu contains Toggles. If so, "click
   * outside to close" functionality will be disabled (unless
   * overridden by "outsideClickClose") & dedicated "close" button
   * will be rendered.
   */
  findMenuToggles(): void {
    this._hasToggles = this._menuSections.some((sect) => {
      return (
        sect.type === FluidContextMenuItemType.TOGGLE ||
        sect.type === FluidContextMenuItemType.TOGGLE_GROUP
      );
    });
  }

  /**
   * Creates PopperJS instance & sets options based on Prop values.
   * Ensures dropdown menu container & trigger elements are defined
   * to prevent errors.
   */
  createPopperInstance(): void {
    if (!!this._popperInstance || !this._containerRef || !this._triggerRef)
      return;

    this._popperInstance = createPopper(this._triggerRef, this._containerRef, {
      strategy: this.absolutePosition ? 'absolute' : 'fixed',
      placement:
        this.menuAlignment === FluidAlignment.LEFT
          ? 'bottom-start'
          : 'bottom-end',
      modifiers: [
        {
          name: 'eventListeners',
          enabled: this._menuOpen,
        },
        {
          name: 'offset',
          options: {
            offset: ({ placement }) => {
              return this.asWrapper
                ? [0, 8]
                : placement.includes('start')
                ? [-8, 8]
                : [8, 8];
            },
          },
        },
        PopperJSModifiers.flipModifier(this.block),
        PopperJSModifiers.preventOverflowModifier(this.block),
        matchWidthModifier(this.block),
      ],
    });
  }

  /**
   * Updates PopperJS instance When "absolutePosition" or
   * "menuAlignment" Props are updated.
   */
  updatePopperOptions(): void {
    this._popperInstance?.setOptions((options) => ({
      ...options,
      strategy: this.absolutePosition ? 'absolute' : 'fixed',
      placement:
        this.menuAlignment === FluidAlignment.LEFT
          ? 'bottom-start'
          : 'bottom-end',
      modifiers: [
        ...options.modifiers,
        PopperJSModifiers.flipModifier(this.block),
        PopperJSModifiers.preventOverflowModifier(this.block),
        matchWidthModifier(this.block),
      ],
    }));
  }

  /**
   * Sets reference to dropdown menu element.
   */
  setMenuRef(el: HTMLElement): void {
    if (this._containerRef === el) return;
    this._containerRef = el;
  }

  /**
   * Sets Toggle Group reference(s).
   * @param el - Toggle Group element
   * @param id - "id" attribute of Toggle Group element
   */
  setToggleGroupRef(el: HTMLFluidToggleGroupElement, id: string): void {
    this._toggleGroupRefs[id] = el;
  }

  /**
   * Called when a Context Menu link is clicked. Hides menu & emits the
   * link object to calling component.
   * @param link - the link that was clicked
   * @param event - if link is an anchor tag, the event to cancel
   */
  handleLinkClick(link: FluidContextMenuLink, event: MouseEvent): void {
    (!!link.route || !!link.externalLink) && event.preventDefault();
    this.toggleMenu();
    this.linkClicked.emit(link);
  }

  /**
   * Called when a Context Menu Toggle changes. Context Menu listens
   * for the "valueChange" event that FLUID Toggle emits when its
   * :checked status changes, then uses the emitted value to set the
   * `selected` property of the corresponding FluidContextMenuToggle.
   * The entire `menuSections` config is then emitted to the calling
   * component to handle.
   * @param checked - FLUID Toggle :checked status (from event)
   * @param toggle - the Context Menu Toggle that was clicked
   */
  handleToggleChange(checked: boolean, toggle: FluidContextMenuToggle): void {
    toggle.selected = checked;
    this.toggleChanged.emit(this._menuSections);
  }

  /**
   * Called when a Toggle Group's state changes. Context Menu listens
   * for a "toggleState" event emitted by FLUID Toggle Group, then
   * uses the emitted "state" to set the "selected" property of all
   * FluidContextMenuToggles in the appropriate FluidContextMenuSection.
   * The entire "menuSections" config is then emitted to the calling
   * component to handle.
   * @param toggleState - "state" of all Toggles within Toggle Group
   * @param toggles - FluidContextMenuToggles in the Toggle Group
   */
  handleToggleGroupChange(
    toggleState: FluidKeyedObject<ToggleAllValue>,
    toggles: FluidContextMenuToggle[]
  ): void {
    toggles.forEach(
      (tgl) => (tgl.selected = toggleState[tgl.controlName] as boolean)
    );
    this.toggleChanged.emit(this._menuSections);
  }

  /**
   * Opens the dropdown menu when mouse enters the component bounds &
   * closes the menu after a delay when mouse leaves. Called when
   * "mouseEnter" or "mouseLeave" event occurs on the component's root
   * container element.
   * @param mouseInBounds - whether the method was triggered on "mouseEnter" or "mouseLeave"
   */
  handleMouseMove(mouseInBounds: boolean): void {
    clearTimeout(this._closeTimeout);
    this._mouseInBounds = mouseInBounds;

    if (!this.openOnHover || (!this.outsideClickClose && !mouseInBounds))
      return;
    mouseInBounds ? this.toggleMenu(true) : this.delayCloseMenu();
  }

  /**
   * Toggles the dropdown menu open/closed when the "trigger" element is
   * clicked UNLESS "openOnHover" is true (allows enter/space keys to
   * toggle menu regardless).
   * @param event - click event that occurred on the "trigger" element
   */
  handleTriggerClick(event: MouseEvent): void {
    if (this.openOnHover && event.detail > 0) return;
    this.toggleMenu();
  }

  /**
   * Sets Context Menu's dropdown menu to "display: none" when closed
   * (AFTER "fade out" animation ends). This ensures that the hidden
   * menu doesn't inadvertently affect the DOM layout around it.
   */
  hideDropdownMenu(): void {
    if (this._menuOpen) return;
    this._menuHidden = true;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    if (!this._menuSections || !this._menuSections.length) return;

    return (
      <Host
        data-menu-open={this._menuOpen}
        class={{ 'fluid-context-menu--block': this.block }}>
        <div
          class={{
            [`fluid-context-menu ${this.theme}`]: true,
            'block-layout': this.block,
            disabled: this.disabled,
          }}
          onMouseEnter={() => this.handleMouseMove(true)}
          onMouseLeave={() => this.handleMouseMove(false)}
          onClick={() =>
            Utils.nextTick().then(() => this._popperInstance?.update().then())
          }>
          <button
            type='button'
            ref={(el) => (this._triggerRef = el as HTMLElement)}
            tabIndex={this.asWrapper || this.disabled ? -1 : 0}
            onClick={(e) => this.handleTriggerClick(e)}
            aria-haspopup='true'
            aria-expanded={String(!!this._menuOpen)}
            aria-controls={this.menuId}
            disabled={this.disabled}
            class={{
              'menu-trigger': true,
              'as-wrapper': this.asWrapper,
              disabled: this.disabled,
            }}>
            <slot />
          </button>
          <DropdownMenu
            host={this}
            menuId={this.menuId}
            menuOpen={this._menuOpen}
            zIndex={this.zIndex}
            tabIndex={-1}
            onAnimationEnd={() => this.hideDropdownMenu()}
            class={{
              'inline-sections':
                this.menuSectionStyle === FluidMenuSectionStyle.INLINE,
              'menu-hidden': !this._popperInstance || this._menuHidden,
            }}>
            <ContextMenuContent
              host={this}
              hasToggles={this._hasToggles}
              i18nStore={this._i18nStore}
              fluidTranslate={this.fluidTranslate}
              closeLabel={this.closeLabel}
            />
          </DropdownMenu>
        </div>
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for dropdown menu.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  /**
   * Usually, Context Menu's "actions" (i.e. "close" button) are NOT
   * displayed when "outsideClickClose" is true. This Prop overrides
   * the default functionality to display the "close" button regardless
   * of "outsideClickClose" value.
   * @internal
   */
  @Prop({ mutable: true }) displayMenuActions: boolean;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidContextMenuTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
}

export interface FluidContextMenuTranslationOptions {
  title?: string;
  closeButton?: {
    label?: string;
  };
}
